// src/components/AddProviderDialog.js

import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Grid,
  CircularProgress,
} from '@mui/material';
import axios from 'axios';
import { API_BASE_URL } from '../constants/constants';

const AddProviderDialog = ({ open, onClose, showSnackbar, refreshData }) => {
  // Form state variables
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [whatsapp, setWhatsapp] = useState('');
  const [address, setAddress] = useState('');
  const [pincode, setPincode] = useState('');

  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  useEffect(() => {
    if (open) {
      // Reset form when dialog opens
      setName('');
      setEmail('');
      setPhoneNumber('');
      setWhatsapp('');
      setAddress('');
      setPincode('');
      setFormErrors({});
    }
  }, [open]);

  // Form validation function
  const validateForm = () => {
    const errors = {};
    const phoneRegex = /^[6-9]\d{9}$/;
    const emailRegex = /^\S+@\S+\.\S+$/;
    const pincodeRegex = /^\d{6}$/;

    if (!name.trim()) errors.name = 'Name is required';
    if (email && !emailRegex.test(email)) errors.email = 'Invalid email format';
    if (!phoneNumber) {
      errors.phoneNumber = 'Phone number is required';
    } else if (!phoneRegex.test(phoneNumber)) {
      errors.phoneNumber = 'Phone number must be 10 digits starting with 6-9';
    }
    if (whatsapp && !phoneRegex.test(whatsapp)) {
      errors.whatsapp = 'WhatsApp number must be 10 digits starting with 6-9';
    }
    if (!address.trim()) errors.address = 'Address is required';
    if (!pincode) {
      errors.pincode = 'Pincode is required';
    } else if (!pincodeRegex.test(pincode)) {
      errors.pincode = 'Pincode must be 6 digits';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handler for form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      showSnackbar('Please fix the errors in the form.', 'error');
      return;
    }

    setLoading(true);
    try {
      const newProvider = {
        name,
        email: email || undefined,
        phoneNumber,
        whatsapp: whatsapp || undefined,
        address,
        pincode,
      };

      const response = await axios.post(`${API_BASE_URL}/providers`, newProvider);
      if (response.status === 201) {
        showSnackbar('Provider created successfully.', 'success');
        onClose();
        refreshData();
      } else {
        showSnackbar('Failed to create provider.', 'error');
      }
    } catch (error) {
      console.error('Error creating provider:', error);
      showSnackbar(error.response?.data?.message || 'Error creating provider.', 'error');
    }
    setLoading(false);
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>Create New Provider</DialogTitle>
      <DialogContent>
        <Grid container spacing={2} sx={{ marginTop: '10px' }}>
          {/* Name */}
          <Grid item xs={12}>
            <TextField
              required
              label="Name"
              fullWidth
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={!!formErrors.name}
              helperText={formErrors.name}
            />
          </Grid>

          {/* Email */}
          <Grid item xs={12}>
            <TextField
              label="Email"
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!formErrors.email}
              helperText={formErrors.email}
            />
          </Grid>

          {/* Phone Number */}
          <Grid item xs={12}>
            <TextField
              required
              label="Phone Number"
              fullWidth
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              error={!!formErrors.phoneNumber}
              helperText={formErrors.phoneNumber}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* WhatsApp Number */}
          <Grid item xs={12}>
            <TextField
              label="WhatsApp Number"
              fullWidth
              value={whatsapp}
              onChange={(e) => setWhatsapp(e.target.value)}
              error={!!formErrors.whatsapp}
              helperText={formErrors.whatsapp}
              inputProps={{ maxLength: 10 }}
            />
          </Grid>

          {/* Address */}
          <Grid item xs={12}>
            <TextField
              required
              label="Address"
              fullWidth
              multiline
              minRows={2}
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              error={!!formErrors.address}
              helperText={formErrors.address}
            />
          </Grid>

          {/* Pincode */}
          <Grid item xs={12} sm={6}>
            <TextField
              required
              label="Pincode"
              fullWidth
              value={pincode}
              onChange={(e) => setPincode(e.target.value)}
              error={!!formErrors.pincode}
              helperText={formErrors.pincode}
              inputProps={{ maxLength: 6 }}
            />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions>
        {loading ? (
          <CircularProgress size={24} />
        ) : (
          <>
            <Button onClick={onClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleSubmit} variant="contained" color="primary">
              Create
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AddProviderDialog;
